
// TeamManagementSystem.jsx
import React, { useState, useEffect } from "react";
import { ChevronDown, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../context/AuthContext";
import { setAuthToken } from "../api";
import api from "../api";
import "../styles/TeamManagementSystem.css";
import Footer from "./Footer";
import TopNavBar from "./TopNavBar";

import Sidebar from "./Sidebar";
import toast from 'react-hot-toast';
// import ApplyLeaveForm from "./ApplyLeaveForm";
import ApplyTimesheetForm from "./Timesheet";
import AttendanceHistory from "./AttendanceHistory";
import TimeLogs from "./TimeLogs";
import ApplyWFHForm from "./ApplyWFHForm";
import LeaveHistory from "./LeaveHistory";
import { checkIn, checkOut, getStatus } from "../services/attendanceService";
import { getMyEmployeeDetails } from "../api/employeeApi";
import { getLeavesByEmployee, isWFH } from "../services/leaveApiService";
import {
  captureCurrentPosition,
  describeRecordLocation,
  hasSeenLocationNotice,
  markLocationNoticeSeen,
} from "../utils/geolocation";
import LocationPrivacyNotice from "./LocationPrivacyNotice";

export default function TeamManagementSystem() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { user, token, login, logout } = useAuth();

  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [displayCheckIn, setDisplayCheckIn] = useState(null);
  const [displayCheckOut, setDisplayCheckOut] = useState(null);
  const [notifications, setNotifications] = useState(5);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isOnLeaveToday, setIsOnLeaveToday] = useState(false);

  // Location-based check-in. Holds the raw attendance record so the location
  // detail (address / coordinates / distance) can be derived in one place.
  const [locationRecord, setLocationRecord] = useState(null);
  const [capturingLocation, setCapturingLocation] = useState(false);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);

  // Dynamic menus
  const [allMenus, setAllMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  /* ============================
     FETCH USER DETAILS
  ============================ */
  useEffect(() => {
    if (!accounts?.length || !token) return;
    const email = accounts[0]?.username;
    if (!email) return;

    const fetchUser = async () => {
      try {
        const res = await getMyEmployeeDetails(email, token);
        const data = res.data;

        login(token, {
          empId: data.employeeId,
          name: data.fullName,
          designation: data.designation,
          email: data.email,
          reportingTo: data.reportingTo,
          department: data.department,
          role: data.role,
        });

        setAuthToken(token);
      } catch (err) {
        console.error("Failed to fetch employee details:", err.response?.data || err);
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, [accounts, token]);

  /* ============================
     FETCH LEAVE STATUS
  ============================ */
  useEffect(() => {
    if (!user || !token) return;

    const fetchLeaveStatus = async () => {
      try {
        const leaves = await getLeavesByEmployee(user.empId, token);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const onLeave = leaves.some(leave => {
          if (leave.status !== 'APPROVED') return false;

          // Ignore Work From Home (WFH) - it shouldn't block timesheets or attendance
          if (isWFH(leave)) return false;

          const startDate = new Date(leave.start_date || leave.startDate);
          startDate.setHours(0, 0, 0, 0);

          const endDate = new Date(leave.end_date || leave.endDate);
          endDate.setHours(23, 59, 59, 999);

          return today >= startDate && today <= endDate;
        });

        setIsOnLeaveToday(onLeave);
      } catch (err) {
        console.error("Failed to check leave status", err);
      }
    };

    fetchLeaveStatus();
  }, [user, token]);

  /* ============================
     FETCH MENUS FROM DB
  ============================ */
  useEffect(() => {
    if (!user) return;

    const fetchMenus = async () => {
      try {
        const res = await api.get("/api/menus/with-roles");
        setAllMenus(res.data || []);
      } catch (err) {
        console.error("Failed to fetch menus:", err);
      } finally {
        setLoadingMenus(false);
      }
    };

    fetchMenus();
  }, [user]);

  /* ============================
     FILTER MENUS BY ROLE
  ============================ */
  const allowedMenus =
    user && allMenus.length
      ? allMenus.filter((menu) => menu.roles?.includes(user.role))
      : [];

  /* ============================
     INITIALIZE ACTIVE MENU
  ============================ */
  useEffect(() => {
    if (!loadingUser && !loadingMenus && allowedMenus.length > 0 && !activeSubMenu) {
      setActiveSubMenu(allowedMenus[0].menu_name);
    }
  }, [loadingUser, loadingMenus, allowedMenus, activeSubMenu]);

  /* ============================
     FETCH CHECK-IN STATUS
  ============================ */
  useEffect(() => {
    if (!user || !token) return;

    const fetchStatus = async () => {
      try {
        const status = await getStatus(token);
        if (status?.check_in) {
          const checkInDate = new Date(status.check_in);
          const now = new Date();

          // Surface the location the active check-in was recorded from.
          setLocationRecord(status);

          const isSameDay =
            checkInDate.getFullYear() === now.getFullYear() &&
            checkInDate.getMonth() === now.getMonth() &&
            checkInDate.getDate() === now.getDate();

          if (!status.check_out) {
            if (isSameDay) {
              const serverTime = checkInDate.getTime();
              setIsCheckedIn(true);
              setCheckInTime(serverTime);
              setElapsedSeconds(Math.floor((now.getTime() - serverTime) / 1000));
              setDisplayCheckIn(serverTime);
              setDisplayCheckOut(null);
            } else {
              // Check-in was from a PREVIOUS day and didn't check out!
              // Auto check-out at Day End (23:59:59) of that check-in date
              const dayEnd = new Date(checkInDate);
              dayEnd.setHours(23, 59, 59, 999);

              setIsCheckedIn(false);
              setCheckInTime(null);
              setElapsedSeconds(0);
              setDisplayCheckIn(checkInDate.getTime());
              setDisplayCheckOut(dayEnd.getTime());
            }
          } else {
            const checkOutDate = new Date(status.check_out);
            const isOutSameDay =
              checkOutDate.getFullYear() === now.getFullYear() &&
              checkOutDate.getMonth() === now.getMonth() &&
              checkOutDate.getDate() === now.getDate();

            if (!isSameDay && !isOutSameDay) {
              setIsCheckedIn(false);
              setCheckInTime(null);
              setElapsedSeconds(0);
              setDisplayCheckIn(null);
              setDisplayCheckOut(null);
            } else {
              setIsCheckedIn(false);
              setCheckInTime(null);
              setElapsedSeconds(0);
              setDisplayCheckIn(checkInDate.getTime());
              setDisplayCheckOut(checkOutDate.getTime());
            }
          }
        } else {
          setIsCheckedIn(false);
          setCheckInTime(null);
          setElapsedSeconds(0);
          setDisplayCheckIn(null);
          setDisplayCheckOut(null);
          setLocationRecord(null);
        }
      } catch (err) {
        console.error("Status fetch error:", err);
        setIsCheckedIn(false);
      }
    };

    fetchStatus();
  }, [user, token]);

  /* ============================
     TIMER FOR ELAPSED TIME
  ============================ */
  useEffect(() => {
    let interval;
    if (isCheckedIn && checkInTime) {
      interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - checkInTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCheckedIn, checkInTime]);

  /* ============================
     HANDLE CHECK-IN / CHECK-OUT
  ============================ */
  /**
   * Check in, optionally with the device position.
   *
   * @param {boolean} withLocation - false skips the location request entirely,
   *   used when the employee declines the privacy notice.
   */
  const performCheckIn = async () => {
    if (!token) return toast.error("Token missing. Please refresh.");
    try {
      // Capture the device location so the API can tag this check-in as
      // in-office or out-of-office. Location is now mandatory.
      setCapturingLocation(true);
      const position = await captureCurrentPosition();
      setCapturingLocation(false);
      const coords = position.coords;

      if (!coords) {
        toast.error("Unable to capture your location. Please allow location access and try again.");
        return;
      }

      const record = await checkIn(token, coords);

      const now = Date.now();
      setIsCheckedIn(true);
      setCheckInTime(now);
      setElapsedSeconds(0);
      setDisplayCheckIn(now);
      setDisplayCheckOut(null);
      setLocationRecord(record || null);

      if (record?.location_status === "in-office") {
        toast.success(`Checked in from ${record.office_name || "the office"}`);
      } else if (record?.location_status === "out-office") {
        toast.success("Checked in - recorded as out of office");
      } else {
        toast.success("Checked in successfully!");
      }
    } catch (err) {
      setCapturingLocation(false);
      console.error("Checkin error:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Check-in failed");
    }
  };

  /**
   * Show the privacy notice once, before the browser's own permission prompt,
   * so the employee learns what is collected and why before being asked.
   */
  const handleCheckIn = () => {
    if (!hasSeenLocationNotice()) {
      setShowPrivacyNotice(true);
      return;
    }
    performCheckIn();
  };

  const handlePrivacyAccept = () => {
    markLocationNoticeSeen();
    setShowPrivacyNotice(false);
    performCheckIn();
  };

  const handlePrivacyDecline = () => {
    setShowPrivacyNotice(false);
    toast.error("Unable to capture your location. Please allow location access and try again.");
  };

  const handleCheckOut = async () => {
    if (!token) return toast.error("Token missing. Please refresh.");
    try {
      // Check if timesheet for today is submitted
      try {
        const tsResponse = await api.get("/api/timesheet/history");
        const timesheets = tsResponse.data?.data || [];
        const todayStr = new Date().toISOString().split('T')[0];

        const hasSubmittedToday = timesheets.some(ts => {
          if (!ts.starttime) return false;
          const tsDateStr = new Date(ts.starttime).toISOString().split('T')[0];
          return tsDateStr === todayStr && ts.hasbeensubmitted === 'Yes';
        });

        if (!hasSubmittedToday) {
          toast.error("Please submit your timesheet for today before checking out.");
          setActiveSubMenu("Timesheets");
          return;
        }
      } catch (tsErr) {
        console.error("Failed to verify timesheet status", tsErr);
        toast.error("Unable to verify timesheet status. Please try again.");
        return;
      }

      // Capture the current check-in time from state before clearing it
      const currentCheckIn = checkInTime;

      // Check-out location is recorded separately - people often leave the
      // office before checking out.
      setCapturingLocation(true);
      const { coords } = await captureCurrentPosition();
      setCapturingLocation(false);

      if (!coords) {
        toast.error("Unable to capture your location. Please allow location access and try again.");
        return;
      }

      await checkOut(token, coords);

      const checkoutTime = new Date().toISOString();
      const checkinTimeISO = currentCheckIn ? new Date(currentCheckIn).toISOString() : null;

      setIsCheckedIn(false);
      setCheckInTime(null);
      setElapsedSeconds(0);
      setDisplayCheckOut(new Date(checkoutTime).getTime());

      toast.success("Checked out successfully!");
    } catch (err) {
      setCapturingLocation(false);
      console.error("Checkout error:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Check-out failed");
    }
  };

  /* ============================
     UTILITIES
  ============================ */
  // Label, detail text and map link for the current check-in's location.
  const location = locationRecord ? describeRecordLocation(locationRecord, "check_in") : null;

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ").filter(p => p && p.toLowerCase() !== "undefined");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (name || "U").substring(0, 2).toUpperCase();
  };

  /* ============================
     RENDER
  ============================ */
  return (
    <div className="page container">
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <LocationPrivacyNotice
        open={showPrivacyNotice}
        onAccept={handlePrivacyAccept}
        onDecline={handlePrivacyDecline}
      />

      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="main" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', padding: 0 }}>
        <div className="hamburger" onClick={toggleSidebar}>☰</div>

        <TopNavBar
          user={user}
          notifications={0}
          toggleSidebar={toggleSidebar}
          logout={logout}
        />

        {/* Banner */}
        <div className="tms-banner-wrapper">
          <div className="tms-banner">
            <div className="tms-banner-tabs">
              {/* <div className="tms-banner-tab-overview"><span>Overview</span></div> */}
            </div>
          </div>
        </div>

        <div className="tms-content-wrapper">
          {/* Left panel */}
          <div className="tms-left-panel">
            <div className="tms-employee-card">
              <div className="tms-profile-avatar">{user ? getInitials(user.name || user.empId) : "SN"}</div>
              <h2 className="tms-profile-name">
                {user?.empId} - {user?.name?.toLowerCase().includes("undefined") ? (user?.name.split(" ")[0] || "Employee") : (user?.name || "Employee")}
              </h2>
              <p className="tms-profile-designation">{user?.designation || "Employee"}</p>
              <div className="tms-status-wrapper">
                <span className={isCheckedIn ? "tms-status-badge tms-status-in" : "tms-status-badge tms-status-out"}>
                  {isCheckedIn ? "In" : "Out"}
                </span>
                {location && (
                  <span
                    className={`tms-location-badge tms-location-${location.tone}`}
                    title={location.title}
                  >
                    <MapPin size={12} />
                    {location.label}
                  </span>
                )}
              </div>

              {/* Where the check-in was actually recorded from. Shown for
                  out-of-office too, not just when an office matched. */}
              {location?.detail && (
                <p className="tms-location-note">
                  {location.detail}
                  {location.mapUrl && (
                    <>
                      {" "}
                      <a
                        className="tms-location-map-link"
                        href={location.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on map
                      </a>
                    </>
                  )}
                  {location.lowConfidence && (
                    <span className="tms-location-warning"> · Low GPS confidence</span>
                  )}
                </p>
              )}
              <div className="tms-timer-wrapper">
                <div className="tms-timer-value">{formatTime(elapsedSeconds)}</div>
              </div>
              <button
                onClick={isCheckedIn ? handleCheckOut : handleCheckIn}
                className={isCheckedIn ? "tms-check-button tms-check-button-out" : "tms-check-button tms-check-button-in"}
                disabled={isOnLeaveToday || capturingLocation}
                style={isOnLeaveToday || capturingLocation ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {capturingLocation
                  ? "Getting location..."
                  : isCheckedIn ? "Check-out" : "Check-in"}
              </button>

              <div style={{ marginTop: '16px', fontSize: '13px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'center' }}>
                {displayCheckIn && (
                  <div><strong>Check-in:</strong> {new Date(displayCheckIn).toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                )}
                {displayCheckOut && (
                  <div><strong>Check-out:</strong> {new Date(displayCheckOut).toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                )}
              </div>

              {isOnLeaveToday && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px', textAlign: 'center', fontWeight: '500' }}>You are on leave today</p>}
            </div>
          </div>

          {/* Right panel */}
          <div className="tms-right-panel">
            {/* Subnav */}
            <div className="tms-subnav">
              <div className="tms-subnav-inner">
                {allowedMenus.filter((menu) => menu.menu_name !== "Leave")
                  .map((menu) => {
                    let displayName = menu.menu_name;
                    if (displayName === "Profile") displayName = "Work From Home";

                    return (
                      <button
                        key={menu.menu_id}
                        onClick={() => setActiveSubMenu(displayName)}
                        className={activeSubMenu === displayName ? "tms-subnav-tab tms-subnav-tab-active" : "tms-subnav-tab"}
                      >
                        {displayName}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Main Content */}
            <div className="tms-main-content">
              <div className="tms-main-content-inner">
                {/* {activeSubMenu === "Leave" && <ApplyLeaveForm user={user} />} */}
                {activeSubMenu === "Timesheets" && <ApplyTimesheetForm user={user} isOnLeaveToday={isOnLeaveToday} />}
                {activeSubMenu === "Attendance" && <AttendanceHistory user={user} token={token} />}
                {activeSubMenu === "Time Logs" && <TimeLogs user={user} token={token} />}
                {activeSubMenu === "Work From Home" && <ApplyWFHForm user={user} token={token} />}
                {activeSubMenu === "Approvals" && <LeaveHistory user={user} embedded={true} />}
                {activeSubMenu &&
                  !["Timesheets", "Attendance", "Time Logs", "Work From Home", "Approvals"].includes(activeSubMenu) && (
                    <div className="tms-main-card">
                      <p className="tms-main-card-text">{activeSubMenu} - Yet to be developed</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>

    </div>
  );
}